// The loanlens-dashboard admin tool (RichNoteEditor, Tiptap-based) stores
// product/sales notes as Tiptap JSON documents — { type: "doc", content: [...] }
// — not plain strings. DsaLens's note UI must flatten this to plain text for
// copy/share, but also render it with the same formatting (bold, headings,
// lists, links, images, tables) the dashboard editor shows — not just raw text.

const BLOCK_TYPES = new Set([
  'doc', 'paragraph', 'heading', 'listItem',
  'bulletList', 'orderedList', 'blockquote',
  'tableRow', 'table', 'codeBlock',
]);

function extractNode(node: Record<string, unknown>): string {
  if (typeof node.text === 'string') return node.text;
  const content = Array.isArray(node.content) ? (node.content as Record<string, unknown>[]) : [];
  const inner = content.map(extractNode).join('');
  return BLOCK_TYPES.has(node.type as string) ? `${inner}\n` : inner;
}

/** Flattens a Tiptap JSON doc to plain text; passes plain strings through unchanged. */
export function richTextToPlainText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (!value || typeof value !== 'object') return '';
  return extractNode(value as Record<string, unknown>).replace(/\n{3,}/g, '\n\n').trim();
}

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Only allow http(s) URLs through into href/src attributes — blocks javascript: etc. */
function safeUrl(url: unknown): string | null {
  if (typeof url !== 'string') return null;
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : null;
  } catch {
    return null;
  }
}

function applyMarks(text: string, marks: Record<string, unknown>[] | undefined): string {
  if (!Array.isArray(marks)) return text;
  return marks.reduce((html, mark) => {
    switch (mark.type) {
      case 'bold': return `<strong>${html}</strong>`;
      case 'italic': return `<em>${html}</em>`;
      case 'strike': return `<s>${html}</s>`;
      case 'code': return `<code>${html}</code>`;
      case 'link': {
        const href = safeUrl((mark.attrs as Record<string, unknown> | undefined)?.href);
        return href ? `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${html}</a>` : html;
      }
      default: return html;
    }
  }, text);
}

function textAlignStyle(attrs: Record<string, unknown> | undefined): string {
  const align = attrs?.textAlign;
  return align === "right" || align === "center" || align === "justify"
    ? ` style="text-align:${align}"`
    : "";
}

function nodeToHtml(node: Record<string, unknown>): string {
  const type = node.type as string | undefined;
  const attrs = node.attrs as Record<string, unknown> | undefined;
  const content = Array.isArray(node.content) ? (node.content as Record<string, unknown>[]) : [];
  const inner = () => content.map(nodeToHtml).join('');

  switch (type) {
    case 'doc': return inner();
    case 'text': return applyMarks(escapeHtml(node.text as string || ''), node.marks as Record<string, unknown>[] | undefined);
    case 'paragraph': return `<p${textAlignStyle(attrs)}>${inner() || '<br>'}</p>`;
    case 'heading': {
      const level = Math.min(Math.max(Number(attrs?.level) || 1, 1), 6);
      return `<h${level}${textAlignStyle(attrs)}>${inner()}</h${level}>`;
    }
    case 'bulletList': return `<ul>${inner()}</ul>`;
    case 'orderedList': return `<ol>${inner()}</ol>`;
    case 'listItem': return `<li>${inner()}</li>`;
    case 'blockquote': return `<blockquote>${inner()}</blockquote>`;
    case 'codeBlock': return `<pre><code>${inner()}</code></pre>`;
    case 'hardBreak': return '<br>';
    case 'horizontalRule': return '<hr>';
    case 'image': {
      const src = safeUrl(attrs?.src);
      if (!src) return '';
      const alt = typeof attrs?.alt === 'string' ? escapeHtml(attrs.alt) : '';
      return `<img src="${escapeHtml(src)}" alt="${alt}">`;
    }
    case 'table': return `<table style="border-collapse:collapse;width:100%;margin:0.5em 0"><tbody>${inner()}</tbody></table>`;
    case 'tableRow': return `<tr>${inner()}</tr>`;
    case 'tableHeader': return `<th style="border:1px solid rgba(148,163,184,0.6);padding:6px 10px;text-align:left;font-weight:600;background:rgba(148,163,184,0.15)">${inner() || '&nbsp;'}</th>`;
    case 'tableCell': return `<td style="border:1px solid rgba(148,163,184,0.6);padding:6px 10px">${inner() || '&nbsp;'}</td>`;
    default: return inner();
  }
}

/** Renders a Tiptap JSON doc to sanitized HTML; wraps plain strings as paragraphs. */
export function richTextToHtml(value: unknown): string {
  if (!value) return '';
  if (typeof value === 'string') {
    if (!value.trim()) return '';
    return value.split('\n').map(line => `<p>${escapeHtml(line)}</p>`).join('');
  }
  if (typeof value !== 'object') return '';
  return nodeToHtml(value as Record<string, unknown>);
}
